/**
 * static-map.js
 */

import React from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { UserContext } from 'contexts/user/user-context';
import defaultMap from 'assets/images/default-map.jpg';
import Spinner from 'components/spinner';
import { Tooltip } from '@mui/material';
import { getCoordinatesFromAddress } from 'utils/location';


const defaultWidth = 175;
const defaultRatio = 1.2;


const useStyles = makeStyles(theme => ({ 
  root: {
    width: defaultWidth,
    height: defaultWidth / defaultRatio,
    position: 'relative',
    borderRadius: 4,
    overflow: 'hidden',
    '& img': {
      width: '100%',
      height: 'auto',
    }
  },
  placeholder: {    
    opacity: 0.2
  },
  loading: {
    justifyContent: 'center'
  },
}));

export default function StaticMap(props) {
  const {
    longitude, 
    latitude,
    location,
  } = props;
  const classes = useStyles();
  const { user } = React.useContext(UserContext);
  const [url, setUrl] = React.useState();
  const [loaded, setLoaded] = React.useState(false);
  const [coords, setCoords] = React.useState();
  const { 
    address1, 
    address2, 
    city, 
    region, 
    country
  } = location || {}; 

  const address = location ? `${address1}${address2 ? ` ${address2}` : ''}, ${city}, ${region} ${country}` : null;
  const addressUrl = location ? 
    `${address1}${address2 ? ` ${address2}` : ''} ${city} ${region}`.replace(/ /g, '+')
    : null;

  const mapBoxImageUrl = React.useCallback((latitude, longitude) => {
    // const style = "streets-v11";
    // const style = "satellite-streets-v11";
    const style = "outdoors-v11";
    const zoomLevel = 15;
    const width = 240;
    const height = 200;
    return `https://api.mapbox.com/styles/v1/mapbox/${style}/static/pin-s-dog-park+f74e4e(${longitude},${latitude})/${longitude},${latitude},${zoomLevel},0,0/${width}x${height}?access_token=${user.apiKeys.MapBox}`;
  }, [user.apiKeys.MapBox]);


  React.useEffect(() => {
    if (longitude && latitude) {
      setCoords({ latitude, longitude });
      setUrl(mapBoxImageUrl(latitude, longitude));
    } else if (address) {
      getCoordinatesFromAddress(address, user.apiKeys.Google).then(
        coords => {
          setCoords(coords);
          setUrl(mapBoxImageUrl(coords.latitude, coords.longitude));
        },
        error => {
          setUrl(defaultMap);
        }
      );
    } else {
      setUrl(defaultMap);
    }
    
  }, [longitude, latitude, address, mapBoxImageUrl, user.apiKeys.Google]);


  return (
    <div className={classes.root}>

      {/* place-holder / default map */}
      {(!url || !loaded) && 
      <img 
        src={defaultMap} 
        className={`absolute-fill ${classes.placeholder}`} 
        alt="Event Map Placeholder" 
      />}

      {/* spinner */}
      {(!url || !loaded) && 
      <div className={`flex-hc absolute-fill ${classes.loading}`}>
        <Spinner size={30} />
      </div>}

      {/* address-based map */}
      {url && !coords &&
        <img 
          src={url} 
          alt="Event Map Icon" 
          onLoad={() => setLoaded(true)}
         />
      }

      {/* coords-based map */}
      {url && coords &&
      <a 
        href={`https://www.google.com/maps/place/${addressUrl || `${coords.latitude},${coords.longitude}`}`}
        target="_blank"
        rel="noopener noreferrer"
      >
        <Tooltip 
          arrow 
          placement="top"
          title="Show this location in Google Maps" 
        >
          <img 
            src={url} 
            alt="Event Map Icon" 
            onLoad={() => setLoaded(true)} 
          />
        </Tooltip>
      </a>}

    </div>   
  );
}